(ns pfo.mcp.controller
  "Keechma controller for the user's connected external MCP servers
   (backend: /api/v1/mcp/connections)."
  (:require [pfo.api :as api :refer [process-error]]
            [promesa.core :as p]
            [pfo.util :as util]
            [keechma.next.controller :as ctrl]))

(derive :mcp-connections :keechma/controller)
(defmethod ctrl/handle :mcp-connections
  [{:keys [state*] :as ctrl} event {:keys [uid params errors-atom on-success on-error]}]
  (case event
    :load (->> (api/ajaxp "/mcp/connections/" {:params {}})
               (p/map (fn [conns] (ctrl/transact ctrl #(reset! state* (or conns [])))))
               (process-error))
    :create (->> (api/ajaxp "/mcp/connections/"
                            {:method :post :params (util/remove-nil-values params)})
                 (p/map (or on-success (fn [_])))
                 (process-error errors-atom))
    :edit (->> (api/ajaxp (str "/mcp/connections/" uid "/")
                          {:method :put :params (util/remove-nil-values params)})
               (p/map (or on-success (fn [_])))
               (process-error errors-atom))
    :update (->> (api/ajaxp (str "/mcp/connections/" uid "/")
                            {:method :patch :params params})
                 (p/map (or on-success (fn [_])))
                 (p/error (fn [e] (when on-error (on-error e)) (throw e)))
                 (process-error))
    ;; Authenticated XHR that returns the authorization-server URL; we then navigate the browser there.
    ;; On failure (e.g. the server is unreachable during discovery) on-error gets the error so the
    ;; caller can show it in-row instead of a generic global flash.
    :authorize (->> (api/ajaxp (str "/mcp/connections/" uid "/authorize") {:params {}})
                    (p/map (fn [resp] (util/set-location (:authorize_url resp))))
                    (p/error (fn [e] (if on-error (on-error e) (throw e)))))
    :delete (->> (api/ajaxp (str "/mcp/connections/" uid "/")
                            {:method :delete})
                 (p/map (or on-success (fn [_])))
                 (process-error))
    :clear (reset! state* nil)
    nil))

;; Connections available to the acting user (via their families) — for naming external tools in chat.
;; Available to any user, unlike :mcp-connections (license-admin pool).
(derive :mcp-available :keechma/controller)
(defmethod ctrl/handle :mcp-available
  [{:keys [state*] :as ctrl} event {:keys [uid on-error]}]
  (case event
    :load (->> (api/ajaxp "/mcp/available/" {:params {}})
               (p/map (fn [conns] (ctrl/transact ctrl #(reset! state* (or conns [])))))
               (process-error))
    ;; Authenticated XHR that returns the authorization-server URL; we then navigate the browser there.
    ;; On failure (e.g. the app server is unreachable during discovery) we hand the error to on-error so
    ;; the caller can show it in-place, instead of surfacing a generic global flash.
    :authorize (->> (api/ajaxp (str "/mcp/connections/" uid "/authorize") {:params {}})
                    (p/map (fn [resp] (util/set-location (:authorize_url resp))))
                    (p/error (fn [e] (if on-error (on-error e) (throw e)))))
    :clear (reset! state* nil)
    nil))

;; Family apps: oauth2 family-mode connections a family admin authorizes on the family's behalf.
(derive :mcp-family-apps :keechma/controller)
(defmethod ctrl/handle :mcp-family-apps
  [{:keys [state*] :as ctrl} event {:keys [family-id uid auth-mode token enabled on-success on-error]}]
  (case event
    :load (->> (api/ajaxp (str "/mcp/families/" family-id "/apps") {:params {}})
               (p/map (fn [apps] (ctrl/transact ctrl #(reset! state* (or apps [])))))
               (process-error))
    ;; Authorize for a specific family: family_id goes as a query param the backend reads.
    :authorize (->> (api/ajaxp (str "/mcp/connections/" uid "/authorize") {:params {:family_id family-id}})
                    (p/map (fn [resp] (util/set-location (:authorize_url resp))))
                    (p/error (fn [e] (if on-error (on-error e) (throw e)))))
    ;; The family admin sets this connection's per-family sub-mode (family / per_user / "" to disable).
    :set-mode (->> (api/ajaxp (str "/mcp/families/" family-id "/apps/" uid)
                              {:method :patch :params {:auth_mode auth-mode}})
                   (p/map (or on-success (fn [_])))
                   (p/error (fn [e] (when on-error (on-error e)) (throw e)))
                   (process-error))
    ;; Set the bearer token for a family-controlled bearer connection (family or per-user, by sub-mode).
    :set-token (->> (api/ajaxp (str "/mcp/families/" family-id "/apps/" uid "/token")
                               {:method :put :params {:token token}})
                    (p/map (or on-success (fn [_])))
                    (p/error (fn [e] (when on-error (on-error e)) (throw e)))
                    (process-error))
    ;; The family admin enables/disables this connection for the family.
    :set-enabled (->> (api/ajaxp (str "/mcp/families/" family-id "/apps/" uid "/enabled")
                                 {:method :patch :params {:enabled enabled}})
                      (p/map (or on-success (fn [_])))
                      (p/error (fn [e] (when on-error (on-error e)) (throw e)))
                      (process-error))
    :clear (reset! state* nil)
    nil))

;; Per-family access grants: which configured connections a family may use (license admin, family card).
(derive :mcp-family-connections :keechma/controller)
(defmethod ctrl/handle :mcp-family-connections
  [{:keys [state*] :as ctrl} event {:keys [family-id uid enabled on-success on-error]}]
  (case event
    :load (->> (api/ajaxp (str "/mcp/families/" family-id "/connections/") {:params {}})
               (p/map (fn [conns] (ctrl/transact ctrl #(reset! state* (or conns [])))))
               (process-error))
    :set (->> (api/ajaxp (str "/mcp/families/" family-id "/connections/" uid "/")
                         {:method :patch :params {:enabled enabled}})
              (p/map (or on-success (fn [_])))
              (p/error (fn [e] (when on-error (on-error e)) (throw e)))
              (process-error))
    :clear (reset! state* nil)
    nil))
